import { Button, Container, Grid, Typography } from '@mui/material'
import StudentsPng from '../../../Assets/Images/students.png'
import { TextFieldWithLabel } from '../../../Components/TextField'
import { useEffect, useState } from 'react'
import { SelectWithLabel } from '../../../Components/SelectWithLabel'
import RemoveIcon from '@mui/icons-material/Remove'
import { useScrollTop } from '../../../hooks/use-scrollTop'
import { type CaregiverValidationState, type CaregiverShortFormState } from '../types'
import { scrollIntoView } from '../../../hooks/useScrollIntoView'
import { CheckBoxWithCustomLabel } from '../../../Components/CustomLabel/CheckboxWithCustomLabel'
import { sendGet } from '../../../hooks/use-fetch'
import { type lookup } from '../../../core/types'
import { MapModule, useBingMaps } from '../../../hooks/use-bingMaps'
import { parseAddress } from '../PortalHelpers'
import { AsYouType } from 'libphonenumber-js'
import { toast } from 'react-toastify'

interface CaregiverShortFormProps {
  isdId?: string
  pageState: CaregiverShortFormState
  languageList: lookup[]
  setPageState: (state: CaregiverShortFormState) => void
  onContinue: () => void
  onBack: () => void
  disableNoEmail?: boolean
  disableHomeless?: boolean
  disableNoPhone?: boolean
}

export function CaregiverShortForm (props: CaregiverShortFormProps): JSX.Element {
  useScrollTop()
  const { pageState, setPageState } = props
  const [showSecondaryPhone, setShowSecondaryPhone] = useState(pageState.secondaryPhone !== null)
  const [contactMethods, setContactMethods] = useState<lookup[]>([])
  const [location, setLocation] = useState<string>('')
  const [validationState, setValidationState] = useState<CaregiverValidationState>({
    firstName: { isDirty: false, isValid: false },
    lastName: { isDirty: false, isValid: false },
    email: { isDirty: false, isValid: false },
    phone: { isDirty: false, isValid: false },
    preferredContactMethodId: { isDirty: false, isValid: false },
    address: { isDirty: false, isValid: false }
  })

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response: methods, error: err2 } = await sendGet('/ContactMethod/GetAll', {})
      if (err2[0] === '') {
        setContactMethods(methods)
      }
    }

    if (contactMethods.length === 0) {
      void fetchData()
    }
  }, [props.isdId])

  const handleHideSecondaryPhone = (): void => {
    setPageState({ ...pageState, secondaryPhone: null })
    setShowSecondaryPhone(false)
  }

  useEffect(() => {
    if (validationState.address.isDirty) {
      setPageState({
        ...pageState,
        address: location
      })
      setValidationState({
        ...validationState,
        address: {
          isDirty: true,
          isValid: validateFormField(pageState.address, 'address')
        }
      })
    }
  }, [location])

  const handlePlaceSelected = async (place: Microsoft.Maps.ISuggestionResult): Promise<void> => {
    setLocation(place.address.formattedAddress)
  }

  const handleAddressChange = (address: string, validateOnly?: boolean): void => {
    setValidationState({ ...validationState, address: { isDirty: true, isValid: validateFormField(address, 'address') } })

    if (validateOnly !== true) {
      setPageState({ ...pageState, address })
    } else {
      try {
        const location = parseAddress(address)
        validateLocation(location)
      } catch { }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const { validateLocation } = useBingMaps({ modules: [MapModule.AutoSuggest], selectFunction: handlePlaceSelected, inputID: 'address', onResultVerified: () => {} })

  const handleChange = (name: string, value: any, validateOnly?: boolean): void => {
    if (validateOnly !== true) {
      setPageState({ ...pageState, [name]: value })
    }

    const fieldToValidate = name === 'doesNotHaveEmail' ? 'email' : name === 'doesNotHavePhone' ? 'phone' : name === 'currentlyHomeless' ? 'address' : name
    setValidationState({ ...validationState, [fieldToValidate]: { isDirty: true, isValid: validateFormField(value, name) } })
  }

  function isValidEmail (email: string): boolean {
    const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return emailRegex.test(email)
  }

  const validateFormField = (value: any, name: string): boolean => {
    if (name === 'firstName') {
      return value !== ''
    } else if (name === 'lastName') {
      return value !== ''
    } else if (name === 'email') {
      return value !== '' || pageState.doesNotHaveEmail
    } else if (name === 'phone') {
      return value !== '' || pageState.doesNotHavePhone
    } else if (name === 'preferredContactMethodId') {
      return value !== null
    } else if (name === 'address') {
      return value !== '' || pageState.currentlyHomeless
    }

    return true
  }

  const validateForm = (): boolean => {
    const firstName = validateFormField(pageState.firstName, 'firstName')
    const lastName = validateFormField(pageState.lastName, 'lastName')
    const email = validateFormField(pageState.email, 'email')
    const phone = validateFormField(pageState.phone, 'phone')
    const preferredContactMethod = validateFormField(pageState.preferredContactMethodId, 'preferredContactMethodId')
    const address = validateFormField(pageState.address, 'address')

    let emailValid = true
    if (pageState.email !== '' && !isValidEmail(pageState.email)) {
      emailValid = false
      toast.error('Please enter a valid email address')
    }

    setValidationState({
      firstName: { isDirty: true, isValid: firstName },
      lastName: { isDirty: true, isValid: lastName },
      email: { isDirty: true, isValid: email },
      phone: { isDirty: true, isValid: phone },
      preferredContactMethodId: { isDirty: true, isValid: preferredContactMethod },
      address: { isDirty: true, isValid: address }
    })

    const scrollTo = !firstName ? 'firstName' : !lastName ? 'lastName' : !email ? 'email' : !phone ? 'phone' : !preferredContactMethod ? 'preferredContactMethodId' : !address ? 'address' : null
    if (scrollTo !== null) {
      scrollIntoView(scrollTo)
    }

    return firstName && lastName && email && emailValid && phone && preferredContactMethod && address
  }

  const handleContinue = (): void => {
    const isValid = validateForm()

    if (isValid) {
      props.onContinue()
    }
  }

  return <>
    <img src={StudentsPng} alt='students' className='students-image' />
    <Container>
      <Grid container>
        <Grid item xs={12} sx={{ my: 1 }}>
          <Typography variant='h4'>About you</Typography>
          <Typography variant='body1'>To get started, please provide us with key information about the Primary Caregiver.</Typography>
        </Grid>
        <Grid item xs={12}>
          <TextFieldWithLabel
            label={'Please enter your first name.'}
            name='firstName'
            maxLength={50}
            onChange={(e) => { handleChange(e.target.name, e.target.value) }}
            // onBlur={(e) => { handleChange(e.target.name, e.target.value, true) }}
            value={pageState.firstName}
            showRequiredText={validationState.firstName.isDirty && !validationState.firstName.isValid}
            required={true}
            error={validationState.firstName.isDirty && !validationState.firstName.isValid}
          />
        </Grid>
        <Grid item xs={12}>
          <TextFieldWithLabel
            label={'Please enter your last name.'}
            name='lastName'
            maxLength={50}
            onChange={(e) => { handleChange(e.target.name, e.target.value) }}
            // onBlur={(e) => { handleChange(e.target.name, e.target.value, true) }}
            value={pageState.lastName}
            showRequiredText={validationState.lastName.isDirty && !validationState.lastName.isValid}
            required={true}
            error={validationState.lastName.isDirty && !validationState.lastName.isValid}
          />
        </Grid>
        <Grid item xs={12}>
          <TextFieldWithLabel
            label={'What is your email?'}
            name='email'
            maxLength={50}
            data-testid='email'
            type='email'
            onChange={(e) => { handleChange(e.target.name, e.target.value) }}
            // onBlur={(e) => { handleChange(e.target.name, e.target.value, true) }}
            value={pageState.email}
            showRequiredText={validationState.email.isDirty && !validationState.email.isValid}
            required={!pageState.doesNotHaveEmail}
            error={validationState.email.isDirty && !validationState.email.isValid}
          />
        </Grid>
        {props.disableNoEmail !== true && (
          <Grid item xs={12}>
            <CheckBoxWithCustomLabel
              name={'doesNotHaveEmail'}
              value={pageState.doesNotHaveEmail}
              setValue={(e) => { handleChange('doesNotHaveEmail', e) }}
              edge={'start'}
              data-testid='does-not-have-email'
              buttonClassName='no-horiz-padding'
              textClassName='vertical-center-align checkbox-label'
              checkboxClassName='no-horiz-padding'
              labelName='IF_CAREGIVER_NO_EMAIL'
              isdId={props.isdId ?? 0}
              defaultLabelText='I do not have an email'
              defaultTooltipText='This should only be checked if this individual does not have access to email. Email is our primary method of contacting families.'
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextFieldWithLabel
            label={'What is your phone number?'}
            name='phone'
            maxLength={50}
            data-testid='phone'
            value={new AsYouType('US').input(pageState.phone)}
            onChange={(e) => { handleChange(e.target.name, e.target.value) }}
            // onBlur={(e) => { handleChange(e.target.name, e.target.value, true) }}
            showRequiredText={validationState.phone.isDirty && !validationState.phone.isValid}
            required={!pageState.doesNotHavePhone}
            error={validationState.phone.isDirty && !validationState.phone.isValid}
          />
        </Grid>
        {!showSecondaryPhone &&
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant='text' data-testid='add-secondary-phone' onClick={() => { setShowSecondaryPhone(true) }}>+ Add Secondary Phone Number</Button>
          </Grid>}
        {showSecondaryPhone && <Grid container item xs={12} spacing={2}>
          <Grid item xs={10}>
            <TextFieldWithLabel
              label={'What is your secondary phone number?'}
              name='secondaryPhone'
              maxLength={50}
              data-testid='secondaryPhone'
              onChange={(e) => { handleChange(e.target.name, e.target.value) }}
              value={new AsYouType('US').input(pageState.secondaryPhone ?? '')}
            />
          </Grid>
          <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button variant='text' data-testid='remove-secondary-phone' onClick={handleHideSecondaryPhone}>
              <RemoveIcon />
            </Button>
          </Grid>
        </Grid>}
        {props.disableNoPhone !== true && (
          <Grid item xs={12}>
            <CheckBoxWithCustomLabel
              name={'doesNotHavePhone'}
              value={pageState.doesNotHavePhone}
              setValue={(e) => { handleChange('doesNotHavePhone', e) }}
              edge={'start'}
              data-testid='does-not-have-phone'
              buttonClassName='no-horiz-padding'
              textClassName='vertical-center-align checkbox-label'
              checkboxClassName='no-horiz-padding'
              labelName='IF_CAREGIVER_NO_PHONE'
              isdId={props.isdId ?? 0}
              defaultLabelText='I do not have a phone number'
              defaultTooltipText='This should only be checked if this individual does not have access to a phone.'
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <SelectWithLabel
            width='100%'
            name='preferredContactMethodId'
            value={pageState.preferredContactMethodId ?? ''}
            label='Preferred Contact Method'
            options={contactMethods}
            onChange={(e) => { handleChange(e.target.name, e.target.value) }}
            showRequiredText={validationState.preferredContactMethodId.isDirty && !validationState.preferredContactMethodId.isValid}
            required={true}
            error={validationState.preferredContactMethodId.isDirty && !validationState.preferredContactMethodId.isValid}
          />
        </Grid>
        <Grid item xs={12}>
          <SelectWithLabel
            width='100%'
            name={'primaryLanguageId'}
            data-testid='primary-language'
            value={pageState.primaryLanguageId ?? ''}
            label='Primary Language (if not English)'
            options={props.languageList}
            onChange={(e) => { handleChange(e.target.name, e.target.value) }}
            showRequiredText={true}
            required={false}
          />
        </Grid>
        <Grid item xs={12}>
          <TextFieldWithLabel
            ariaRole='combobox'
            ariaExpanded={false}
            ariaControls='as_containerSearch_address'
            label='Address'
            name='address'
            data-testid="address"
            autoComplete='off'
            // onBlur={(e) => { handleAddressChange(e.target.value, true) }}
            onChange={(e) => { handleAddressChange(e.target.value) }}
            value={pageState.address}
            showRequiredText={validationState.address.isDirty && !validationState.address.isValid}
            required={!pageState.currentlyHomeless}
            error={validationState.address.isDirty && !validationState.address.isValid}
          />
          <div id='searchBoxContainer'></div>
        </Grid>
        {props.disableHomeless !== true && (
          <Grid item xs={12}>
            <CheckBoxWithCustomLabel
              name={'currentlyHomeless'}
              value={pageState.currentlyHomeless ?? false}
              setValue={(e) => { handleChange('currentlyHomeless', e) }}
              edge={'start'}
              buttonClassName='no-horiz-padding'
              textClassName='vertical-center-align checkbox-label'
              checkboxClassName='no-horiz-padding'
              labelName='IF_CAREGIVER_HOMELESS'
              isdId={props.isdId ?? 0}
              defaultLabelText='I am currently experiencing homelessness'
              defaultTooltipText='Check this box if you are currently experiencing homelessness'
            />
          </Grid>
        )}
        <Grid container item xs={12} spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <Button
              className='back-button'
              onClick={props.onBack}
              variant="outlined"
              data-testid='back-button'
            >
              Back
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              data-testid='continue-button'
              className='footer-button'
              onClick={handleContinue}
              variant="contained"
            >
              Continue
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  </>
}
